/**
 * TRACI-474: sign in through Styx (Tire Rack's identity provider).
 *
 * The browser never holds a Styx token. The flow is:
 *   1. makeVerifier + s256 -> POST /api/auth/styx/start -> {authorizeUrl, state}
 *      The PKCE verifier waits in sessionStorage, keyed by state.
 *   2. Styx sends the person back to /callback?code&state.
 *   3. POST /api/auth/styx/session {code, state, codeVerifier} -> {token, expiresAt, email}
 *      The backend token (`trs_...`) is stored in localStorage `traci.session`.
 */
import { getBackendApiUrl } from '../config/backend';

export const SESSION_KEY = 'traci.session';
const PKCE_PREFIX = 'traci.styx.pkce.';
const PKCE_MAX_AGE_MS = 15 * 60 * 1000;
const CONFIG_TIMEOUT_MS = 5000;
/** A token on its last stretch is not worth a request that lands expired. */
const EXPIRY_SKEW_MS = 5000;

export interface StyxSession {
  token: string;
  expiresAt: string | number;
  email: string;
}

export interface AuthConfig {
  styx: {
    enabled: boolean;
    issuer: string;
    authorizationEndpoint: string;
    clientId: string;
  };
}

/** What "Styx off" looks like: no sign-in on offer (TRACI-620 removed the old Duo one). */
export const STYX_OFF: AuthConfig = {
  styx: { enabled: false, issuer: '', authorizationEndpoint: '', clientId: '' },
};

// ── session storage ────────────────────────────────────────────────────────

/** Milliseconds since epoch from an ISO string, epoch seconds or epoch millis. */
export function parseExpiry(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    if (/^\d+(\.\d+)?$/.test(value.trim())) return parseExpiry(Number(value));
    const t = Date.parse(value);
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

/** The stored session as written, expired or not (logout needs its bearer). */
export function readStoredSession(): StyxSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<StyxSession>;
    if (!s || typeof s.token !== 'string' || !s.token.startsWith('trs_')) return null;
    return { token: s.token, expiresAt: s.expiresAt as string | number, email: String(s.email || '') };
  } catch {
    return null;
  }
}

/** The stored session only while it is still good. No refresh exists (8 h, then sign in again). */
export function readStyxSession(now: number = Date.now()): StyxSession | null {
  const s = readStoredSession();
  if (!s) return null;
  const exp = parseExpiry(s.expiresAt);
  if (exp === null || exp - EXPIRY_SKEW_MS <= now) return null;
  return s;
}

export function expiryOf(session: StyxSession): number | null {
  return parseExpiry(session.expiresAt);
}

/** True when `traci.session` holds something, good or lapsed. */
export function hasStoredStyxSession(): boolean {
  return readStoredSession() !== null;
}

export function clearStyxSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // storage unavailable: nothing to clear
  }
}

function storeSession(s: StyxSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify({ token: s.token, expiresAt: s.expiresAt, email: s.email }));
}

// ── PKCE ───────────────────────────────────────────────────────────────────

function base64url(bytes: Uint8Array): string {
  let bin = '';
  bytes.forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 32 random bytes, base64url: 43 characters, inside RFC 7636's 43 to 128. */
export function makeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

export async function s256(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

interface PendingSignIn {
  verifier: string;
  returnTo: string;
  at: number;
}

function pendingKey(state: string): string {
  return PKCE_PREFIX + state;
}

function readPending(state: string): PendingSignIn | null {
  try {
    const raw = sessionStorage.getItem(pendingKey(state));
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingSignIn;
    return p && typeof p.verifier === 'string' ? p : null;
  } catch {
    return null;
  }
}

function prunePending(now: number = Date.now()): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith(PKCE_PREFIX)) continue;
      const p = readPending(key.slice(PKCE_PREFIX.length));
      if (!p || now - (p.at || 0) > PKCE_MAX_AGE_MS) sessionStorage.removeItem(key);
    }
  } catch {
    // storage unavailable
  }
}

/** Only same-origin paths survive: no "//host", no "https://...". */
export function safeReturnPath(path: unknown): string {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return '/';
  if (path.startsWith('/callback') || path.startsWith('/login') || path.startsWith('/signed-out')) return '/';
  return path;
}

/**
 * Whether this page load is the return leg of a Styx sign-in that THIS browser
 * started: a `state` in the URL with a PKCE verifier waiting under it. Anything
 * else (a stray link, another tab's sign-in) is not ours.
 */
export function isStyxCallback(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    const state = params.get('state');
    return !!state && readPending(state) !== null;
  } catch {
    return false;
  }
}

// ── backend calls ──────────────────────────────────────────────────────────

let configPromise: Promise<AuthConfig> | null = null;

/** Test hook: forget the cached /api/auth/config answer. */
export function resetAuthConfigCache(): void {
  configPromise = null;
}

function normalizeConfig(raw: unknown): AuthConfig {
  const r = (raw || {}) as { styx?: Partial<AuthConfig['styx']> };
  const s = r.styx || {};
  const enabled = s.enabled === true && !!s.clientId && !!(s.issuer || s.authorizationEndpoint);
  if (!enabled) return STYX_OFF;
  return {
    styx: {
      enabled: true,
      issuer: String(s.issuer || ''),
      authorizationEndpoint: String(s.authorizationEndpoint || ''),
      clientId: String(s.clientId || ''),
    },
  };
}

/**
 * What the backend says about sign-in. A 404 (backend not deployed yet), a
 * network error or `enabled:false` all mean "Styx off", so either side of this
 * change can ship first. Never throws. Definite answers are cached for the page
 * load; a network error is not, so the next call tries again.
 */
export function fetchAuthConfig(): Promise<AuthConfig> {
  if (configPromise) return configPromise;
  const attempt = (async (): Promise<AuthConfig | null> => {
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), CONFIG_TIMEOUT_MS) : null;
    try {
      const res = await fetch(`${getBackendApiUrl()}/api/auth/config`, { signal: ctl?.signal });
      if (res.status === 404) return STYX_OFF;
      if (!res.ok) return null;
      return normalizeConfig(await res.json());
    } catch {
      return null;
    } finally {
      if (timer) clearTimeout(timer);
    }
  })();
  const cached = attempt.then((cfg) => {
    if (cfg === null) {
      configPromise = null;
      return STYX_OFF;
    }
    return cfg;
  });
  configPromise = cached;
  return cached;
}

/** Start a Styx sign-in: make PKCE, ask the backend for the authorize URL, go there. */
export async function beginStyxSignIn(returnTo: string = '/'): Promise<void> {
  const verifier = makeVerifier();
  const codeChallenge = await s256(verifier);
  const res = await fetch(`${getBackendApiUrl()}/api/auth/styx/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ codeChallenge, redirectUri: `${window.location.origin}/callback` }),
  });
  if (!res.ok) throw new Error(`Could not start sign-in (${res.status})`);
  const data = (await res.json()) as { authorizeUrl?: string; state?: string };
  if (!data.authorizeUrl || !data.state) throw new Error('Could not start sign-in');
  prunePending();
  const pending: PendingSignIn = { verifier, returnTo: safeReturnPath(returnTo), at: Date.now() };
  sessionStorage.setItem(pendingKey(data.state), JSON.stringify(pending));
  window.location.assign(data.authorizeUrl);
}

/** Spend a verifier without exchanging anything (Styx sent back an error). */
export function discardPendingSignIn(state: string): void {
  try { sessionStorage.removeItem(pendingKey(state)); } catch { /* storage unavailable */ }
}

const inflight = new Map<string, Promise<{ returnTo: string; session: StyxSession }>>();

/**
 * Finish a Styx sign-in on /callback. The verifier is spent whatever happens
 * (the state is single-use on the server too), so a retry starts a fresh
 * sign-in. React StrictMode runs effects twice; the same state shares one call.
 */
export function completeStyxCallback(code: string, state: string): Promise<{ returnTo: string; session: StyxSession }> {
  const existing = inflight.get(state);
  if (existing) return existing;
  const run = (async () => {
    const pending = readPending(state);
    if (!pending) throw new Error('No sign-in in progress for this link');
    try {
      const res = await fetch(`${getBackendApiUrl()}/api/auth/styx/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, state, codeVerifier: pending.verifier }),
      });
      if (!res.ok) {
        let detail = '';
        try {
          const body = await res.json();
          detail = typeof body?.detail === 'string' ? body.detail : '';
        } catch {
          // body was not JSON
        }
        const err = new Error(detail || `Sign-in was refused (${res.status})`) as Error & { status?: number };
        err.status = res.status;
        throw err;
      }
      const data = (await res.json()) as Partial<StyxSession>;
      if (!data.token || !String(data.token).startsWith('trs_')) throw new Error('Sign-in returned no session');
      const session: StyxSession = {
        token: data.token,
        expiresAt: (data.expiresAt as string | number) ?? Date.now() + 8 * 60 * 60 * 1000,
        email: String(data.email || ''),
      };
      storeSession(session);
      return { returnTo: safeReturnPath(pending.returnTo), session };
    } finally {
      try { sessionStorage.removeItem(pendingKey(state)); } catch { /* storage unavailable */ }
    }
  })();
  inflight.set(state, run);
  // Keep a failed attempt from being replayed to a later, genuinely new callback.
  run.then(() => undefined, () => undefined).finally(() => { setTimeout(() => inflight.delete(state), 2000); });
  return run;
}

/** Tell the backend to drop the session. Best effort: logout proceeds either way. */
export async function revokeStyxSession(token: string): Promise<void> {
  try {
    await fetch(`${getBackendApiUrl()}/api/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(3000),
    });
  } catch {
    // offline or backend down: the token still expires on its own
  }
}

/** Styx end_session URL, coming back to /signed-out. */
export function endSessionUrl(cfg: AuthConfig, origin: string = window.location.origin): string {
  const base = cfg.styx.issuer.replace(/\/+$/, '');
  const params = new URLSearchParams({
    client_id: cfg.styx.clientId,
    post_logout_redirect_uri: `${origin}/signed-out`,
  });
  return `${base}/session/end?${params.toString()}`;
}
